/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput"; // Needed for date range inputs

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// API
import { api } from "api";

// Material Dashboard 2 React Context
import { useMaterialUIController } from "context";

function SalesDetails() {
    const [controller, dispatch] = useMaterialUIController();
    const { dateRange } = controller;

    const location = useLocation();
    const navigate = useNavigate();
    // Getting type from location state
    const { type, compCode, extras } = location.state || { title: "Sales Breakdown", type: "Sales" };

    // Header is now just the uppercase Type
    const displayHeader = (type || "Sales").toUpperCase();

    // Helper to format date
    const formatDate = (date) => {
        const d = new Date(date);
        let month = "" + (d.getMonth() + 1);
        let day = "" + d.getDate();
        const year = d.getFullYear();

        if (month.length < 2) month = "0" + month;
        if (day.length < 2) day = "0" + day;

        return [year, month, day].join("-");
    };

    const [showDateRange, setShowDateRange] = useState(false);



    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [menu, setMenu] = useState(null);
    // const [typesToFetch, setTypesToFetch] = useState([]); // Removed unused state

    const openMenu = ({ currentTarget }) => setMenu(currentTarget);
    const closeMenu = () => setMenu(null);

    // Date Filter Logic (Copied/Adapted from Dashboard)
    const handleDateFilter = (filterType) => {
        const today = new Date();
        let start = new Date();
        let end = new Date();

        if (filterType === "today") {
            start = today;
            end = today;
        } else if (filterType === "yesterday") {
            const y = new Date(today);
            y.setDate(today.getDate() - 1);
            start = y;
            end = y;
        } else if (filterType === "this_month") {
            start = new Date(today.getFullYear(), today.getMonth(), 1);
            end = today;
        } else if (filterType === "last_month") {
            start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            end = new Date(today.getFullYear(), today.getMonth(), 0);
        } else if (filterType === "this_year") {
            start = new Date(today.getFullYear(), 0, 1);
            end = today;
        }

        dispatch({ type: "DATE_RANGE", value: { start: formatDate(start), end: formatDate(end), type: filterType } });
        setShowDateRange(false);
        closeMenu();
    };

    const [summaryStats, setSummaryStats] = useState(extras || { total: 0, cash: 0, bank: 0 });

    // Fetch transactions & Summary
    useEffect(() => {
        if (!dateRange || !compCode) {
            console.warn("DEBUG: Missing context for fetchTransactions", { dateRange, compCode });
            return;
        }

        const fetchTransactions = async () => {
            setLoading(true);
            try {
                // Map UI type to API vtypes
                // Sales -> SALES, SALES JOURNAL
                // Purchase -> PURCHASE, PURCHASE JOURNAL
                // Bank Receipt -> BANK RECEIPT
                // Payment -> PAYMENT
                // etc.

                let typesToFetch = [];
                const t = (type || "").toUpperCase();

                if (t === "SALES") {
                    typesToFetch = ["SALES", "SALES JOURNAL"];
                } else if (t === "PURCHASE") {
                    typesToFetch = ["PURCHASE", "PURCHASE JOURNAL"];
                } else if (t.includes("SALES") && (t.includes("RETURN") || t.includes("RET"))) {
                    // User confirmed type: SALESRETURN
                    typesToFetch = [
                        "SALESRETURN", "SALES RETURN", "SALES_RETURN", "Sales Return",
                        "SALES RETURNS", "SALES_RETURNS", "Sales Returns"
                    ];
                } else if (t.includes("PURCHASE") && (t.includes("RETURN") || t.includes("RET"))) {
                    // User confirmed type: PURCHASERETURN
                    typesToFetch = [
                        "PURCHASERETURN", "PURCHASE RETURN", "PURCHASE_RETURN", "Purchase Return",
                        "PURCHASE RETURNS", "PURCHASE_RETURNS", "Purchase Returns"
                    ];
                } else if (t === "RECEIPT") {
                    typesToFetch = ["RECEIPTS"];
                } else if (t === "PAYMENT") {
                    typesToFetch = ["PAYMENTS"];
                } else if (t === "CASH & BANK") {
                    // Fetch all liquid types (New API: PAYMENTS and RECEIPTS)
                    typesToFetch = ["RECEIPTS", "PAYMENTS"];
                } else if (t === "PAYABLE" || t.includes("PAYABLE")) {
                    // Special handling for Payable
                    typesToFetch = ["PAYABLE"];
                } else if (t.includes("RECEIVABLE") || t.includes("RECIEVABLE")) {
                    // Special handling for Receivable
                    typesToFetch = ["RECEIVABLE"];
                } else {
                    // Default
                    typesToFetch = [t];
                }

                // Helper to normalize transaction data
                const normalizeData = (dataList, sourceType) => {
                    return dataList.map(item => {
                        const normalized = {};
                        // First normalize all to lowercase
                        for (const key in item) {
                            if (Object.prototype.hasOwnProperty.call(item, key)) {
                                normalized[key.toLowerCase()] = item[key];
                            }
                        }

                        // Map specific API fields to our Table Accessors
                        // Update: For Outstand List Keys are: 
                        // transDate -> Date
                        // balance -> Amount
                        // particular -> Customer
                        // voucherNo -> Voucher No
                        return {
                            ...normalized,
                            vdate: normalized.transDate || normalized.transdate || normalized.voucherdate || normalized.vdate || normalized.transactiondate || normalized["transaction date"],
                            vno: normalized.voucherNo || normalized.voucherno || normalized.vno || normalized.vouchernumber,
                            narr: normalized.particular || normalized.particulars || normalized.narr,
                            // Prioritize particular for Customer column in Outstand List
                            acname: normalized.particular || normalized.particulars || normalized.accountname || normalized.acname || "",
                            amount: normalized.balance || normalized.balanceamount || normalized["balance amount"] || normalized.amount,
                            sourceType: sourceType
                        };
                    });
                };

                // Fetch Summary for Cash & Bank
                if (t === "CASH & BANK" || t === "BANK & CASH LIST") {
                    // setTypesToFetch(["BACA"]);
                    // Parallel fetch: List + Closing Balance
                    const [res, closingRes] = await Promise.all([
                        api.getBankCashList({
                            vdatefrom: dateRange.start,
                            vdateto: dateRange.end,
                            vtype: "BACA", // User specific type BACA
                            compcode: compCode,
                        }),
                        api.getClosingCashBank({
                            companyCode: compCode,
                            asOnDate: dateRange.end
                        }).catch(e => {
                            console.warn("Failed to fetch closing stats", e);
                            return [];
                        })
                    ]);

                    // Process List
                    let dataList = [];
                    if (res && Array.isArray(res.data)) {
                        dataList = res.data;
                    } else if (Array.isArray(res)) {
                        dataList = res;
                    }
                    setTransactions(normalizeData(dataList, "BACA"));

                    // Process Closing Stats
                    if (Array.isArray(closingRes)) {
                        let closedCash = 0;
                        let closedBank = 0;
                        closingRes.forEach(item => {
                            if (item.accGroup === "Cash") closedCash += item.closingAmount;
                            if (item.accGroup === "Banks") closedBank += item.closingAmount;
                        });
                        setSummaryStats({
                            cash: closedCash,
                            bank: closedBank,
                            total: closedCash + closedBank
                        });
                    }
                } else if (t === "PAYABLE" || t.includes("PAYABLE") || t.includes("RECEIVABLE") || t.includes("RECIEVABLE")) {
                    // Use new Outstand List API
                    const vtype = (t.includes("PAYABLE") ? "PAYABLE" : "RECEIVABLE");

                    const res = await api.getOutstandList({
                        vdatefrom: dateRange.start,
                        vdateto: dateRange.end,
                        vtype: vtype,
                        compcode: compCode
                    });

                    let dataList = [];
                    // Handle potential response structures
                    if (res && Array.isArray(res.data)) {
                        dataList = res.data;
                    } else if (res && Array.isArray(res.list)) {
                        dataList = res.list;
                    } else if (Array.isArray(res)) {
                        dataList = res;
                    }

                    setTransactions(normalizeData(dataList, vtype));

                } else {
                    // Standard Logic for other types using getVouchers (Transactions)
                    // setTypesToFetch(typesToFetch); // Removed unused state set

                    const fetchList = async (vtype) => {
                        try {
                            const res = await api.getVouchers({
                                vdatefrom: dateRange.start,
                                vdateto: dateRange.end,
                                vtype: vtype,
                                compcode: compCode
                            });

                            let dataList = [];
                            if (res && Array.isArray(res.data) && res.data.length > 0) {
                                dataList = res.data;
                            } else if (Array.isArray(res) && res.length > 0) {
                                dataList = res;
                            }
                            return dataList;
                        } catch (e) {
                            console.warn(`Failed to fetch ${vtype}`, e);
                            return [];
                        }
                    };

                    const results = await Promise.all(typesToFetch.map(fetchList));
                    const allData = results.flat();

                    // Normalize standard data
                    const normalizedData = normalizeData(allData, "STANDARD");

                    // Sort by date (vdate) descending
                    normalizedData.sort((a, b) => {
                        const dateA = new Date(a.vdate || 0);
                        const dateB = new Date(b.vdate || 0);
                        return dateB - dateA;
                    });

                    setTransactions(normalizedData);
                }
            } catch (error) {
                console.error("Failed to fetch transaction details", error);
            } finally {
                setLoading(false);
            }

            // Debug code removed

        };

        fetchTransactions();
    }, [dateRange, compCode, type]);


    // Columns for DataTable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const columns = useMemo(() => {
        const cols = [
            { Header: "Date", accessor: "vdate", width: "15%" },
            { Header: "Voucher No", accessor: "vno", width: "20%" },
            { Header: "Customer", accessor: "acname", width: "45%" },
            { Header: "Amount", accessor: "amount", width: "20%", align: "left" },
        ];

        return cols;
    }, [type]);

    // Rows for DataTable - Memoized
    const rows = useMemo(() => transactions.map(t => ({
        vdate: t.vdate ? new Date(t.vdate).toLocaleDateString() : "-",
        vno: t.vno || "-",
        acname: t.acname || t.acc_name || t.accountname || "Unknown",
        amount: (
            // User requested "little big size", changed to h6 or subtitle1
            <MDTypography variant="h6" color="dark" fontWeight="bold">
                {Number(t.amount).toLocaleString()}
            </MDTypography>
        ),
    })), [transactions]);

    const tableData = useMemo(() => ({ columns, rows }), [columns, rows]);

    const renderMenu = (
        <Menu
            id="simple-menu"
            anchorEl={menu}
            anchorOrigin={{
                vertical: "top",
                horizontal: "left",
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "right",
            }}
            open={Boolean(menu)}
            onClose={closeMenu}
        >
            <MenuItem onClick={() => handleDateFilter("today")} selected={dateRange.type === "today"} style={{ backgroundColor: dateRange.type === "today" ? "#e3f2fd" : "inherit" }}>Today</MenuItem>
            <MenuItem onClick={() => handleDateFilter("yesterday")} selected={dateRange.type === "yesterday"} style={{ backgroundColor: dateRange.type === "yesterday" ? "#e3f2fd" : "inherit" }}>Yesterday</MenuItem>
            <MenuItem onClick={() => handleDateFilter("this_month")} selected={dateRange.type === "this_month"} style={{ backgroundColor: dateRange.type === "this_month" ? "#e3f2fd" : "inherit" }}>This Month</MenuItem>
            <MenuItem onClick={() => handleDateFilter("last_month")} selected={dateRange.type === "last_month"} style={{ backgroundColor: dateRange.type === "last_month" ? "#e3f2fd" : "inherit" }}>Last Month</MenuItem>
            <MenuItem onClick={() => handleDateFilter("this_year")} selected={dateRange.type === "this_year"} style={{ backgroundColor: dateRange.type === "this_year" ? "#e3f2fd" : "inherit" }}>This Year</MenuItem>
            <MenuItem
                onClick={() => {
                    setShowDateRange(true);
                    dispatch({ type: "DATE_RANGE", value: { ...dateRange, type: "custom" } });
                    closeMenu();
                }}
                selected={dateRange.type === "custom"}
                style={{ backgroundColor: dateRange.type === "custom" ? "#e3f2fd" : "inherit" }}
            >
                Select Date Range
            </MenuItem>
        </Menu>
    );

    return (
        <DashboardLayout>
            <DashboardNavbar showTitle={false} />
            {/* Reduced paddingTop from pt={6} to pt={2} */}
            <MDBox pt={2} pb={3}>
                <MDBox mb={1} display="flex" justifyContent="space-between" alignItems="center">
                    <MDBox display="flex" alignItems="center">
                        <MDTypography variant="h4" fontWeight="bold">
                            {displayHeader}
                        </MDTypography>

                        {/* Date Filter Inputs (Only visible when Select Date Range is clicked) */}
                        {showDateRange && (
                            <MDBox display="flex" alignItems="center" ml={2}>
                                <MDInput
                                    type="date"
                                    variant="outlined"
                                    size="small"
                                    sx={{ mr: 1, backgroundColor: "white" }}
                                    value={dateRange.start}
                                    inputProps={{ max: dateRange.end }}
                                    onChange={(e) => dispatch({ type: "DATE_RANGE", value: { ...dateRange, start: e.target.value, type: "custom" } })}
                                />
                                <MDTypography variant="caption" sx={{ mx: 1 }}>
                                    to
                                </MDTypography>
                                <MDInput
                                    type="date"
                                    variant="outlined"
                                    size="small"
                                    sx={{ backgroundColor: "white" }}
                                    value={dateRange.end}
                                    inputProps={{ min: dateRange.start }}
                                    onChange={(e) => dispatch({ type: "DATE_RANGE", value: { ...dateRange, end: e.target.value, type: "custom" } })}
                                />
                            </MDBox>
                        )}

                        <IconButton
                            size="small"
                            disableRipple
                            color="inherit"
                            sx={{
                                ml: 1,
                                boxShadow: "0rem 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1), 0rem 0.125rem 0.25rem -0.0625rem rgba(0, 0, 0, 0.06)",
                                backgroundColor: "#fff",
                                "&:hover": {
                                    backgroundColor: "#f8f9fa",
                                },
                            }}
                            onClick={openMenu}
                        >
                            <Icon>filter_list</Icon>
                        </IconButton>
                        {renderMenu}
                    </MDBox>

                    <MDBox display="flex" alignItems="center">
                        <MDButton
                            variant="gradient"
                            color="info"
                            onClick={() => navigate("/dashboard")}
                            startIcon={<Icon>arrow_back</Icon>}
                        >
                            Back to Dashboard
                        </MDButton>
                    </MDBox>
                </MDBox>

                {/* Summary Cards using State */}
                {(type === "CASH & BANK" || type === "Cash & Bank") && (
                    <MDBox mb={3}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <Card sx={{ borderLeft: "5px solid #1A73E8" }}>
                                    <MDBox p={2}>
                                        <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                                            Total Cash/Bank Balance
                                        </MDTypography>
                                        <MDTypography variant="h4" fontWeight="bold" color="info">
                                            QAR {(Number(summaryStats.total) || 0).toLocaleString()}
                                        </MDTypography>
                                    </MDBox>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card>
                                    <MDBox p={2}>
                                        <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                                            Cash Total
                                        </MDTypography>
                                        <MDTypography variant="h4" fontWeight="bold" color="dark">
                                            QAR {(Number(summaryStats.cash) || 0).toLocaleString()}
                                        </MDTypography>
                                    </MDBox>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card>
                                    <MDBox p={2}>
                                        <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
                                            Bank Total
                                        </MDTypography>
                                        <MDTypography variant="h4" fontWeight="bold" color="dark">
                                            QAR {(Number(summaryStats.bank) || 0).toLocaleString()}
                                        </MDTypography>
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>
                    </MDBox>
                )}

                <MDBox>

                    <Card>
                        {loading ? (
                            <MDBox p={4} textAlign="center">
                                <CircularProgress color="info" />
                            </MDBox>
                        ) : (
                            <MDBox>
                                {transactions.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        showTotalEntries={true}
                                        isSorted={false}
                                        noEndBorder
                                        canSearch={true}
                                    />
                                ) : (
                                    <MDBox p={3} textAlign="center">
                                        <MDTypography variant="body2" color="text" mb={2}>
                                            No transactions found for this period.
                                        </MDTypography>
                                    </MDBox>
                                )}
                            </MDBox>
                        )}
                    </Card>
                </MDBox>

            </MDBox>
            <Footer />
        </DashboardLayout>
    );
}

export default SalesDetails;
